import React from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { interpolate } from '@format/interpolate';
import { yupResolver } from '@hookform/resolvers/yup';
import { Heading, Input } from '@web/atoms';
import { useExperiment, HIDE_NAME_IN_EMAIL_STEP } from '@web/experiments';
import { FormFieldsContainer } from '../FormFieldsContainer';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useOffer } from '../../offer';
import { onboardingSubmitted, onboardingBackArrowClicked } from '../events';
import { Footer } from '../Footer';
import { Question } from '../Question';
import { StyledForm } from '../StyledForm';
import { useSessionCustomer } from '../../customer';
import { emailOnlySchema, schema } from './schema';
import {
  DidYouKnowTextContainer,
  SubheadlineTextOnly,
  TextOnlyContainer,
} from './styles';

export { schema, emailOnlySchema } from './schema';

export const UserInfoForm = ({
  defaultValues,
  headline,
  emailLabel,
  emailQuestionCaption,
  emailQuestionPrompt,
  firstNameLabel,
  firstNameQuestionPrompt,
  goBack,
  index,
  onSubmit,
  petName,
  bannerMessage,
  ctaButtonText,
}) => {
  const offer = useOffer();
  const reporter = useReporter();
  const { customer } = useSessionCustomer();

  const goBackWithReporter = () => {
    reporter.tag(onboardingBackArrowClicked('email_step', customer));
    goBack();
  };

  const { value: hideNameInput } = useExperiment(
    HIDE_NAME_IN_EMAIL_STEP,
    false,
  );
  const {
    formState: { errors, isSubmitting, isSubmitted, isSubmitSuccessful },
    handleSubmit,
    setError,
    clearErrors,
    control,
    reset,
    getValues,
  } = useForm({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(hideNameInput ? emailOnlySchema : schema),
  });
  const { value } = useExperiment('EMAIL_CAPTURE_DISCOUNT', 'control');
  const submitForm = async data => {
    reporter.tag(onboardingSubmitted(index, 'user_info'));
    const result = await onSubmit(data);
    ApiResult.match(result, {
      error: {
        invalidEmail: () =>
          setError(
            'email',
            {
              type: 'server',
              message: 'Email is invalid',
            },
            { shouldFocus: true },
          ),
      },
    });
    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
          keepErrors: true,
        }),
      ),
    );
  };
  const isTextVariant = value === 'slim_banner';
  const hasBannerDiscount = offer.amount > 0;

  const renderTextAndDiscount = ({ bannerDiscount, bannerMessage }) => {
    const interpolatedBannerMessage = interpolate(bannerMessage, {
      bannerDiscount: bannerDiscount,
    });
    if (interpolatedBannerMessage === '') return;
    return (
      <DidYouKnowTextContainer>
        <TextOnlyContainer>
          <SubheadlineTextOnly>{interpolatedBannerMessage}</SubheadlineTextOnly>
        </TextOnlyContainer>
      </DidYouKnowTextContainer>
    );
  };

  return (
    <StyledForm onSubmit={handleSubmit(submitForm)}>
      <FormFieldsContainer $maxWidth={391}>
        {hasBannerDiscount &&
          isTextVariant &&
          renderTextAndDiscount({
            bannerDiscount: offer.formattedAmount,
            bannerMessage,
          })}
        {headline && (
          <Heading
            typography="heading3"
            position="center"
            headingLevel="h2"
            headingText={headline}
            verticalPadding={1}
          />
        )}
        {!hideNameInput && (
          <Question prompt={firstNameQuestionPrompt} isUIVariant={false}>
            <Controller
              control={control}
              name="firstName"
              render={({ field, fieldState: { error } }) => (
                <Input
                  {...field}
                  error={error?.message}
                  labelText={firstNameLabel}
                  status={error?.message ? 'error' : null}
                  visuallyHideLabel
                  centeredAlign
                />
              )}
            />
          </Question>
        )}
        <Question
          caption={emailQuestionCaption}
          prompt={emailQuestionPrompt}
          isUIVariant={false}
        >
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                onChange={value => {
                  if (errors.email?.type === 'server') {
                    clearErrors('email');
                  }
                  field.onChange(value.trim());
                }}
                labelText={emailLabel}
                error={error?.message}
                status={error?.message ? 'error' : null}
                visuallyHideLabel
                centeredAlign
              />
            )}
          />
        </Question>
      </FormFieldsContainer>

      <Footer
        mobileCTAText={ctaButtonText?.mobileCTA}
        desktopCTAText={ctaButtonText?.desktopCTA}
        isBackButtonDisabled={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        isForwardButtonDisabled={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        isForwardButtonLoading={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        onBackButtonClick={goBackWithReporter}
      />
    </StyledForm>
  );
};

UserInfoForm.displayName = 'UserInfoForm';

UserInfoForm.propTypes = {
  defaultValues: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  emailLabel: PropTypes.string.isRequired,
  headline: PropTypes.string.isRequired,
  emailQuestionCaption: PropTypes.string.isRequired,
  emailQuestionPrompt: PropTypes.string.isRequired,
  firstNameLabel: PropTypes.string.isRequired,
  firstNameQuestionPrompt: PropTypes.string.isRequired,
  goBack: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
  bannerMessage: PropTypes.string,
  ctaButtonText: PropTypes.object,
};
